import { ZoneContextManager } from "@opentelemetry/context-zone";
import {
  CompositePropagator,
  W3CTraceContextPropagator,
} from "@opentelemetry/core";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { XMLHttpRequestInstrumentation } from "@opentelemetry/instrumentation-xml-http-request";
import { B3InjectEncoding, B3Propagator } from "@opentelemetry/propagator-b3";
import { resourceFromAttributes } from "@opentelemetry/resources";
import {
  SimpleSpanProcessor,
  WebTracerProvider,
} from "@opentelemetry/sdk-trace-web";
import { ATTR_SERVICE_NAME } from "@opentelemetry/semantic-conventions";

import { CONFIG } from "config";

// Set an empty provider just in case initialization doesn't happen
export let traceProvider = new WebTracerProvider();

/**
 * Setup OpenTelemetry tracing.
 * - @opentelemetry/instrumentation-document-load
 * - @opentelemetry/instrumentation-fetch
 * - @opentelemetry/instrumentation-xml-http-request
 *
 * Initialize tracing instrumentations functionally due to an issue with the ZoneContextManager
 * in unit tests. The ZoneContextManager is used to track span context across async operations.
 * Even when initialization is wrapped in a conditional block, the declaration is read by JS/Node
 * and processed which results in numerous test failures. The workaround is to separate the
 * tracing provider from the initialization and functionally pass in the trace provider to
 * register its instrumentations.
 *
 * Possibly related to https://github.com/open-telemetry/opentelemetry-js/issues/3030
 */
const initializeTracing = () => {
  // Skip tracing if no collector is defined
  if (!CONFIG.OTEL_COLLECTOR) {
    return;
  }

  const url = new URL(CONFIG.OTEL_COLLECTOR, window.location.origin);

  traceProvider = new WebTracerProvider({
    spanProcessors: [
      new SimpleSpanProcessor(
        new OTLPTraceExporter({
          url: url.href,
        })
      ),
    ],
    resource: resourceFromAttributes({
      [ATTR_SERVICE_NAME]: `keystone-ui-${CONFIG.APP_ENV}`,
    }),
  });

  traceProvider.register({
    contextManager: new ZoneContextManager(),
    propagator: new CompositePropagator({
      propagators: [
        new W3CTraceContextPropagator(),
        new B3Propagator({ injectEncoding: B3InjectEncoding.MULTI_HEADER }),
      ],
    }),
  });

  const matchSageSureSubdomainsOrLocal =
    /^https?:\/\/(.*?\.(icg360\.(org|net|com)|sagesure\.(net|com))|localhost:\d{4})(\/.*)?/;
  const matchAllExceptSageSureOrLocal =
    /^https?:\/\/(?!(.*?\.(icg360\.(org|net|com)|sagesure\.(net|com))|localhost:\d{4}))(\/.*)?/;

  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls: [matchSageSureSubdomainsOrLocal],
        ignoreUrls: [matchAllExceptSageSureOrLocal],
      }),
      new XMLHttpRequestInstrumentation({
        propagateTraceHeaderCorsUrls: [matchSageSureSubdomainsOrLocal],
        ignoreUrls: [matchAllExceptSageSureOrLocal],
      }),
    ],
  });
};

export { initializeTracing };
